import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOrder } from '../context/OrderContext';
import { apiRequest, getDeviceId } from '../utils/api';
import TicketGenerator from '../utils/TicketGenerator';
import ArticleCard from './ArticleCard';
import './SimpleOrderPage.css';
import './ArticleCard.css';

const SimpleOrderPage = () => {
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const { articles, incrementTicketCounter, resetTicketCounter, updatePrice } = useOrder();
  const [quantities, setQuantities] = useState({});
  const [showConfig, setShowConfig] = useState(false);
  const [draftPrices, setDraftPrices] = useState({});

  // Initialize quantities for all articles
  useEffect(() => {
    const initialQuantities = {};
    Object.keys(articles).forEach(category => {
      articles[category].forEach(article => {
        initialQuantities[article.id] = 0;
      });
    });
    setQuantities(initialQuantities);
  }, [articles]);

  useEffect(() => {
    const nextDraftPrices = {};
    Object.keys(articles).forEach(category => {
      articles[category].forEach(article => {
        nextDraftPrices[article.id] = article.price.toString();
      });
    });
    setDraftPrices(nextDraftPrices);
  }, [articles]);

  const handleQuantityChange = (articleId, delta) => {
    setQuantities(prev => ({
      ...prev,
      [articleId]: Math.max(0, prev[articleId] + delta)
    }));
  };

  const handlePrintTickets = async () => {
    // Get all articles with quantity > 0
    const articlesWithQuantity = [];
    Object.keys(articles).forEach(category => {
      articles[category].forEach(article => {
        if (quantities[article.id] > 0) {
          articlesWithQuantity.push({
            ...article,
            quantity: quantities[article.id]
          });
        }
      });
    });

    if (articlesWithQuantity.length === 0) {
      return;
    }

    // Generate all tickets as a single batch
    const batchTicketData = {
      html: '',
      text: ''
    };

    for (const item of articlesWithQuantity) {
      for (let i = 0; i < item.quantity; i++) {
        const ticketData = TicketGenerator.generateSimpleTicket(item);
        batchTicketData.html += ticketData.html + '<div style="border-top: 2px dashed #000; margin: 15px 0; height: 20px;">&nbsp;</div>';
        batchTicketData.text += ticketData.text + '\n\n\n====================================\n\n';

        // Log ticket to API
        await apiRequest('tickets', {
          method: 'POST',
          body: JSON.stringify({
            articleId: item.id,
            articleName: item.name,
            category: item.category,
            price: item.price,
            deviceId: getDeviceId(),
            deviceLabel: navigator.userAgent
          })
        });
        incrementTicketCounter(item.id, 1);
      }
    }

    // Print batch using SmartPos SDK first, then Bluetooth, then direct print
    await TicketGenerator.printWithFallback(batchTicketData);

    // Reset quantities after printing
    const resetQuantities = {};
    Object.keys(quantities).forEach(key => {
      resetQuantities[key] = 0;
    });
    setQuantities(resetQuantities);
  };

  const handleResetCounters = () => {
    if (window.confirm('¿Está seguro de que desea reiniciar todos los contadores a cero?')) {
      resetTicketCounter();
      alert('Contadores reiniciados exitosamente');
    }
  };

  const commitPriceChange = async (article) => {
    const rawPrice = draftPrices[article.id];
    const newPrice = parseFloat(rawPrice);

    if (!Number.isFinite(newPrice) || newPrice < 0) {
      setDraftPrices(prev => ({
        ...prev,
        [article.id]: article.price.toString()
      }));
      return;
    }

    if (newPrice !== article.price) {
      await updatePrice(article.id, newPrice);
      setDraftPrices(prev => ({
        ...prev,
        [article.id]: newPrice.toString()
      }));
    }
  };

  const handlePriceDraftChange = (articleId, newPrice) => {
    setDraftPrices(prev => ({
      ...prev,
      [articleId]: newPrice
    }));
  };

  const commitAllPriceChanges = async () => {
    for (const article of getAllArticles()) {
      await commitPriceChange(article);
    }
  };

  const handleConfigToggle = async () => {
    if (showConfig) {
      await commitAllPriceChanges();
    }
    setShowConfig(!showConfig);
  };

  const getAllArticles = () => {
    const allArticles = [];
    Object.keys(articles).forEach(category => {
      articles[category].forEach(article => {
        allArticles.push({ ...article, category });
      });
    });
    return allArticles;
  };

  const getTotalItems = () => {
    return Object.values(quantities).reduce((sum, qty) => sum + qty, 0);
  };

  return (
    <div className="simple-order-page">
      {/* Floating Print Button - Hidden when config is active */}
      {!showConfig && (
        <button 
          className="floating-print-button"
          onClick={handlePrintTickets}
          disabled={getTotalItems() === 0}
          title="Imprimir todos los tickets seleccionados"
        >
          <span className="ticket-icon">🎫</span>
          Imprimir Tickets ({getTotalItems()})
        </button>
      )}

      <div className="header">
        <h1>Rio Haus - Sistema de Tickets <span className="app-version">v.2</span></h1>
        <div className="header-buttons">
          <span className="user-badge">{user.display_name}</span>
          {isAdmin && (
            <button 
              className="config-button"
              onClick={() => navigate('/admin')}
            >
              📊 Admin
            </button>
          )}
          {isAdmin && (
            <button 
              className="config-button"
              onClick={handleConfigToggle}
            >
              ⚙️ Configurar Precios
            </button>
          )}
          <button 
            className="reset-button"
            onClick={logout}
          >
            � Salir
          </button>
        </div>
      </div>

      {showConfig && (
        <div className="config-section">
          <h3>Configuración de Precios</h3>
          <div className="price-grid">
            {getAllArticles().map(article => (
              <div key={article.id} className="price-item">
                <label>{article.name}</label>
                <input
                  type="number"
                  step="0.01"
                  value={draftPrices[article.id] ?? article.price}
                  onChange={(e) => handlePriceDraftChange(article.id, e.target.value)}
                  onBlur={() => commitPriceChange(article)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    }
                  }}
                  className="price-input"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="main-content">
        {!showConfig && (
          <div className="items-section">
            <div className="items-grid">
              {getAllArticles().map(article => (
                <ArticleCard
                  key={article.id}
                  article={article}
                  quantity={quantities[article.id]}
                  onQuantityChange={handleQuantityChange}
                />
              ))}
            </div>
          </div>
        )}

        <div className="action-section">
          <div className="summary">
            <h3>Total de Items: {getTotalItems()}</h3>
          </div>
          
          <button 
            className="print-button"
            onClick={handlePrintTickets}
            disabled={getTotalItems() === 0}
          >
            🎫 Imprimir Tickets ({getTotalItems()})
          </button>
        </div>

              </div>
    </div>
  );
};

export default SimpleOrderPage;
